import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import './Planning.css';
import { useAuth } from "../../AuthContext.js";
import configData from "../../configure";
import { FaEdit, FaChevronDown, FaChevronUp, FaTrash } from 'react-icons/fa';

function Planning({ classId, orgId }) {
  const [courseId, setCourseId] = useState('');
  const [courseDetails, setCourseDetails] = useState(null);
  const [coursePlan, setCoursePlan] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editSubtopicId, setEditSubtopicId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { subtopic, planId }
  const { axios: authAxios, token, userRole } = useAuth();
  const [classData, setClassData] = useState(null);
  const [faculties, setFaculties] = useState([]);
  const [expandedTerms, setExpandedTerms] = useState({});
  const [showSubtopicDropdown, setShowSubtopicDropdown] = useState(false);
  const [selectedSubtopics, setSelectedSubtopics] = useState([]);

  const [newUnit, setNewUnit] = useState({
    termName: '',
    startDate: '',
    endDate: '',
    status: '',
    facultyId: '',
  });


  // === FETCH FACULTIES ===
  const fetchFaculties = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authAxios.get(
        `${configData.GET_FACULTIES_BY_ORG}/${orgId}`,
        {
          params: { userType: "FACULTY", pageSize: 500 },
          headers: { accept: "application/hal+json", Authorization: token },
        }
      );

      if (res.data?.items) {
        const extracted = res.data.items.map(item => ({
          ...item.user,
          employeeId: item.facultyInfo?.employeeId || "N/A",
        }));
        setFaculties(extracted);
      } else {
        setFaculties([]);
      }
    } catch (err) {
      console.warn('Faculty fetch error:', err);
      // Fallback gracefully without setting blocking error screen
      setFaculties([]);
    } finally {
      setLoading(false);
    }
  }, [authAxios, token]);

  // === FETCH CLASS & COURSES ===
  useEffect(() => {
    if (!classId) return;
    const fetch = async () => {
      try {
        const res = await authAxios.get(`${configData.GET_CLASS_SCHEDULE_BY_ID}/${classId}`);
        setClassData(res.data);
      } catch (err) {
        setError('Failed to fetch class data.');
      }
    };
    fetch();
  }, [classId, authAxios]);

  useEffect(() => {
    const validCourseIds = (classData?.courseIds || []).filter(id => id && id !== 'string');
    if (validCourseIds.length > 0 && !courseId) {
      setCourseId(validCourseIds[0]);
    }
  }, [classData, courseId]);

  useEffect(() => {
    const fetchCourses = async () => {
      const validCourseIds = (classData?.courseIds || []).filter(id => id && id !== 'string');
      if (validCourseIds.length === 0 || !token || !courseId || courseId === 'string') {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const mainRes = await authAxios.get(`${configData.GET_COURSE_BY_ID}/${courseId}`, {
          headers: { Authorization: token }
        });
        const promises = validCourseIds.map(async (id) => {
          try {
            const res = await authAxios.get(`${configData.GET_COURSE_BY_ID}/${id}`, {
              headers: { Authorization: token }
            });
            return res.data;
          } catch (err) {
            console.warn(`Failed to fetch course details for courseId ${id}:`, err);
            return null;
          }
        });
        const responses = await Promise.all(promises);
        const courses = responses.filter(c => c !== null);

        setCourseDetails(mainRes.data);
        setAllCourses(courses);
      } catch (err) {
        setError('Failed to fetch courses.');
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, [classData, courseId, token, authAxios]);

  // === FETCH COURSE PLAN ===
  useEffect(() => {
    const fetchPlan = async () => {
      if (!classId) return;
      try {
        const res = await authAxios.get(
          `${configData.GET_COURSE_PLAN_BY_CLASS}${classId}`,
          { headers: { Authorization: token } }
        );
        setCoursePlan(res.data);
      } catch (err) {
        setError('Failed to fetch course plan.');
      }
    };
    fetchPlan();
  }, [classId, token, authAxios]);

  useEffect(() => { fetchFaculties(); }, [fetchFaculties]);

  // === MODAL AUTO-FILL ===
  useEffect(() => {
    if (showModal && !isEditMode) {
      const assigned = getAssignedFaculties();
      if (assigned.length === 1) {
        setNewUnit(prev => ({ ...prev, facultyId: assigned[0].userId }));
      }
      setSelectedSubtopics([]);
    } else if (showModal && isEditMode) {
      setSelectedSubtopics([editSubtopicId]);
    }
  }, [showModal, isEditMode, editSubtopicId]);

  // === HANDLERS ===
  const handleCourseChange = (e) => setCourseId(e.target.value);

  const openAddModal = () => {
    setIsEditMode(false);
    setEditSubtopicId(null);
    setNewUnit({ termName: '', startDate: '', endDate: '', status: '', facultyId: '' });
    setSelectedSubtopics([]);
    setShowModal(true);
  };

  const openEditModal = (subtopic, coursePlanId) => {
    setIsEditMode(true);
    setEditSubtopicId(subtopic.subtopicName);
    setNewUnit({
      termName: subtopic.termName,
      startDate: subtopic.startDate ? new Date(subtopic.startDate).toISOString().split('T')[0] : '',
      endDate: subtopic.endDate ? new Date(subtopic.endDate).toISOString().split('T')[0] : '',
      status: subtopic.status,
      facultyId: getFacultyIdForPlan(coursePlanId),
    });
    setSelectedSubtopics([subtopic.subtopicName]);
    setShowModal(true);
  };

  const openDeleteModal = (subtopic, planId) => {
    setDeleteTarget({ subtopic, planId });
    setShowDeleteModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setEditSubtopicId(null);
    setNewUnit({ termName: '', startDate: '', endDate: '', status: '', facultyId: '' });
    setSelectedSubtopics([]);
    setShowSubtopicDropdown(false);
    setError('');
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setNewUnit(prev => ({ ...prev, [name]: value }));
  };

  const toggleSubtopic = (subtopic) => {
    if (isEditMode) {
      setSelectedSubtopics([subtopic]);
    } else {
      setSelectedSubtopics(prev =>
        prev.includes(subtopic)
          ? prev.filter(s => s !== subtopic)
          : [...prev, subtopic]
      );
    }
  };

  // === DUPLICATE CHECKS (GLOBAL) ===
  const isSubtopicGloballyPlanned = (plan, subtopic) => {
    return plan.subtopicsPlanList?.some(st => st.subtopicName === subtopic) || false;
  };

  const isTermUsed = (plan, term) => {
    return plan.subtopicsPlanList?.some(st => st.termName === term) || false;
  };

  // === SAVE UNIT ===
  const handleSave = async () => {
    setError('');
    try {
      if (selectedSubtopics.length === 0 || !newUnit.termName || !newUnit.startDate || !newUnit.endDate || !newUnit.status || !newUnit.facultyId) {
        setError('All fields are required.');
        return;
      }
      if (new Date(newUnit.startDate) > new Date(newUnit.endDate)) {
        setError('Start date must be before end date.');
        return;
      }

      const plan = coursePlan?.find(p => p.courseId === courseId && p.facultyId === newUnit.facultyId);
      if (!plan) {
        setError('No plan found for this faculty + course.');
        return;
      }

      if (!isEditMode && isTermUsed(plan, newUnit.termName)) {
        setError(`Term "${newUnit.termName}" is already used.`);
        return;
      }

      const globalDuplicates = selectedSubtopics.filter(sub =>
        isSubtopicGloballyPlanned(plan, sub) && (!isEditMode || sub !== editSubtopicId)
      );
      if (globalDuplicates.length > 0) {
        setError(`Subtopic(s) already planned: ${globalDuplicates.join(', ')}`);
        return;
      }

      const newSubs = isEditMode
        ? [{
            subtopicName: selectedSubtopics[0],
            termName: newUnit.termName,
            startDate: new Date(newUnit.startDate).toISOString(),
            endDate: new Date(newUnit.endDate).toISOString(),
            status: newUnit.status,
          }]
        : selectedSubtopics.map(sub => ({
            subtopicName: sub,
            termName: newUnit.termName,
            startDate: new Date(newUnit.startDate).toISOString(),
            endDate: new Date(newUnit.endDate).toISOString(),
            status: newUnit.status,
          }));

      const updatedList = isEditMode
        ? plan.subtopicsPlanList.map(st =>
            st.subtopicName === editSubtopicId ? newSubs[0] : st
          )
        : [...(plan.subtopicsPlanList || []), ...newSubs];

      const payload = {
        ...plan,
        lastUpdatedBy: "admin",
        lastUpdatedAt: new Date().toISOString(),
        subtopicsPlanList: updatedList,
      };

      await authAxios.put(
        configData.get_resources_resources_course_plan(plan.coursePlanId),
        payload,
        { headers: { Authorization: token, "Content-Type": "application/json" } }
      );

      await refreshCoursePlan();
      closeModal();

    } catch (err) {
      setError(`Save failed: ${err.response?.data?.message || err.message}`);
    }
  };

  // === DELETE UNIT ===
  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const { subtopic, planId } = deleteTarget;
      const plan = coursePlan?.find(p => p.coursePlanId === planId);
      if (!plan) throw new Error("Plan not found");

      const updatedList = plan.subtopicsPlanList.filter(
        st => !(st.subtopicName === subtopic.subtopicName && st.termName === subtopic.termName)
      );

      const payload = {
        ...plan,
        lastUpdatedBy: "admin",
        lastUpdatedAt: new Date().toISOString(),
        subtopicsPlanList: updatedList,
      };

      await authAxios.put(
        configData.get_resources_resources_course_plan(planId),
        payload,
        { headers: { Authorization: token, "Content-Type": "application/json" } }
      );

      await refreshCoursePlan();
      closeDeleteModal();

    } catch (err) {
      setError(`Delete failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const refreshCoursePlan = async () => {
    try {
      const res = await authAxios.get(
        `${configData.GET_COURSE_PLAN}/filter?filterType=class&id=${classId}`,
        { headers: { Authorization: token } }
      );
      setCoursePlan(res.data);
    } catch (err) {
      console.error("Refresh failed:", err);
    }
  };

  // === HELPERS ===
  const getFacultyIdForPlan = (planId) => coursePlan?.find(p => p.coursePlanId === planId)?.facultyId || '';

  const getAssignedFaculties = () => {
    if (!coursePlan || !courseId) return [];
    const ids = [...new Set(coursePlan.filter(p => p.courseId === courseId).map(p => p.facultyId))];
    return faculties.filter(f => ids.includes(f.userId));
  };

  const organizeByTerm = (list) => {
    return list.reduce((acc, st) => {
      acc[st.termName] = acc[st.termName] || [];
      acc[st.termName].push(st);
      return acc;
    }, {});
  };

  const getTermRange = (subs) => {
    const dates = subs.flatMap(s => [s.startDate, s.endDate].filter(Boolean));
    if (!dates.length) return '';
    const min = new Date(Math.min(...dates.map(d => new Date(d))));
    const max = new Date(Math.max(...dates.map(d => new Date(d))));
    return `${min.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} - ${max.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
  };

  const toggleTerm = (term) => {
    setExpandedTerms(prev => ({ ...prev, [term]: !prev[term] }));
  };

  // === RENDER ===
  if (loading) return <div className="planning-container">Loading...</div>;
  if (error && !showModal && !showDeleteModal) return <div className="planning-container error">{error}</div>;
  if (!courseDetails) return <div className="planning-container">No course data.</div>;

  const filteredPlans = coursePlan?.filter(p => p.courseId === courseId) || [];
  const allSubtopics = filteredPlans.flatMap(p =>
    p.subtopicsPlanList.map(st => ({ ...st, coursePlanId: p.coursePlanId }))
  );
  const termGroups = organizeByTerm(allSubtopics);
  const termNames = Object.keys(termGroups);

  return (
    <div className="planning-page">
      <div className="course-selector">
        <select value={courseId} onChange={handleCourseChange}>
          {allCourses.map(c => (
            <option key={c.courseId} value={c.courseId}>
              {c.courseDetails?.courseName} ({c.courseCode})
            </option>
          ))}
        </select>
        <div className="faculty-info">
          <b>Faculty:</b> {getAssignedFaculties().map(f => `${f.firstName} ${f.lastName}`).join(', ') || 'None'}
        </div>
      </div>

      {termNames.length > 0 ? (
        termNames.map((term, idx) => {
          const subs = [...termGroups[term]].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
          const range = getTermRange(subs);
          const isOpen = expandedTerms[term];

          return (
            <div key={idx} className="term-section">
              <div className="term-header" onClick={() => toggleTerm(term)} style={{ cursor: 'pointer' }}>
                <h3>{term} - {courseDetails.courseDetails?.courseName}</h3>
                <span>{range}</span>
                <div className="term-header-actions">
                  {userRole !== 'STUDENT' && (
                    <button className="add-unit-btn" onClick={e => { e.stopPropagation(); openAddModal(); }}>
                      + Add Unit
                    </button>
                  )}
                  {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </div>
              <div className={`term-subtopics ${isOpen ? 'expanded' : ''}`}>
                {subs.map((st, i) => (
                  <div key={i} className="unit-card">
                    <div className="unit-details">
                      <div className="unit-detail-item">
                      <h4>{st.subtopicName}</h4>
                      <span><strong>Start:</strong> {new Date(st.startDate).toLocaleDateString()}</span>
                      <span><strong>End:</strong> {new Date(st.endDate).toLocaleDateString()}</span>
                      <span className={`status ${st.status?.toLowerCase().replace(' ', '-')}`}>
                        <strong>Status:</strong> {st.status}
                      </span>
                      {userRole !== 'STUDENT' && (
                        <div className="unit-actions">
                          <button className="edit-unit-btn" onClick={() => openEditModal(st, st.coursePlanId)}>
                            <FaEdit />
                          </button>
                          <button className="delete-unit-btn" onClick={() => openDeleteModal(st, st.coursePlanId)}>
                            <FaTrash />
                          </button>
                        </div>
                      )}
                    </div>
                     </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      ) : (
        <div className="term-section">
          {userRole !== 'STUDENT' && (
            <button className="add-unit-btn" onClick={openAddModal}>+ Add Unit</button>
          )}
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{isEditMode ? 'Edit Unit' : 'Add New Unit'}</h3>
              <button className="modal-close-btn" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="error">{error}</div>}
              {getAssignedFaculties().length === 0 && (
                <div className="error">No faculty assigned.</div>
              )}

              {/* SUBTOPICS */}
              <div className="cp-form-group">
                <label>Subtopics</label>
                <div className="cp-multiselect-container">
                  <div className="cp-multiselect-input" onClick={() => setShowSubtopicDropdown(!showSubtopicDropdown)}>
                    {selectedSubtopics.length > 0 ? selectedSubtopics.join(', ') : 'Select subtopics'}
                    <span className="dropdown-arrow">{showSubtopicDropdown ? 'Up' : 'Down'}</span>
                  </div>
                  {showSubtopicDropdown && (
                    <div className="cp-multiselect-dropdown">
                      {courseDetails.courseDetails?.topics?.map((topic, idx) => {
                        const plan = coursePlan?.find(p => p.courseId === courseId && p.facultyId === newUnit.facultyId);
                        const isPlanned = plan ? isSubtopicGloballyPlanned(plan, topic) : false;
                        const isDisabled = isEditMode
                          ? topic !== editSubtopicId && isPlanned
                          : isPlanned;

                        return (
                          <label
                            key={idx}
                            className={`cp-multiselect-option ${isDisabled ? 'disabled' : ''}`}
                            style={{ opacity: isDisabled ? 0.5 : 1 }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedSubtopics.includes(topic)}
                              onChange={() => toggleSubtopic(topic)}
                              disabled={isEditMode || isDisabled}
                            />
                            {topic}
                            {isDisabled && !isEditMode && ' (Already planned)'}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="cp-form-group">
                <label>Term Name</label>
                <input
                  type="text"
                  name="termName"
                  value={newUnit.termName}
                  onChange={handleInput}
                  placeholder="e.g., Term 1"
                  disabled={isEditMode}
                />
              </div>

              <div className="form-row">
                <div className="cp-form-group">
                  <label>Start Date</label>
                  <input type="date" name="startDate" value={newUnit.startDate} onChange={handleInput} />
                </div>
                <div className="cp-form-group">
                  <label>End Date</label>
                  <input type="date" name="endDate" value={newUnit.endDate} onChange={handleInput} />
                </div>
              </div>

              <div className="cp-form-group">
                <label>Status</label>
                <select name="status" value={newUnit.status} onChange={handleInput}>
                  <option value="">Select</option>
                  <option value="Planned">Planned</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>

              <div className="cp-form-group">
                <label>Faculty</label>
                <select
                  name="facultyId"
                  value={newUnit.facultyId}
                  onChange={handleInput}
                  disabled={getAssignedFaculties().length === 0 || isEditMode}
                >
                  <option value="">Select</option>
                  {getAssignedFaculties().map(f => (
                    <option key={f.userId} value={f.userId}>
                      {f.firstName} {f.lastName} (ID: {f.employeeId})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button className="cancel-btn" onClick={closeModal}>Cancel</button>
              <button
                className="save-btn"
                onClick={handleSave}
                disabled={getAssignedFaculties().length === 0}
              >
                {isEditMode ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && deleteTarget && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button className="modal-close-btn" onClick={closeDeleteModal}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the following unit?</p>
              <div className="delete-preview">
                <strong>Subtopic:</strong> {deleteTarget.subtopic.subtopicName}<br/>
                <strong>Term:</strong> {deleteTarget.subtopic.termName}<br/>
                <strong>Status:</strong> {deleteTarget.subtopic.status}
              </div>
              {/* <p style={{ color: '#d32f2f', fontWeight: 'bold' }}>This action cannot be undone.</p> */}
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={closeDeleteModal}>Cancel</button>
              <button className="delete-confirm-btn" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Planning;